import DefaultLayout from "../../../layouts/DefaultLayout";
import {Table} from "../../tables/BasicTable";
import React, {useContext, useEffect, useRef, useState} from "react";
import {useAuth} from "../../../providers/authProvider";
import BoxLayout from "../../../layouts/BoxLayout";
import DefaultButton from "../../buttons/DefaultButton";
import {ApiContext} from "../../contexts/ApiContext";

import {useNavigate, useParams} from "react-router-dom";
import H2Header from "../../headers/h2";

import EditHomeVisitModal from "../../modals/EditHomeVisitModal";
import EditChildModal from "../../modals/EditChildModal";
import EditButton from "../../buttons/EditButton";
import EditAdultModal from "../../modals/EditAdultModal";
import MailEditor from "../../mail/MailEditor";
import {generateFinalVisitInfoEmailContent} from "../../mail/HuistbezoekLaatsteInfo";
import {generateInvoiceAndGroteBoekEmailContent} from "../../mail/FactuurEnGroteBoek";


interface HomeVisitDetails {
    ID: number;
    Date: string;
    MaxChildAge: number;
    StartTime: string;
    EndTime: string;
    NumberOfChildren: number;
    NumberOfAdults: number;
    SoftSignUpDeadline: string;
    HardSignUpDeadline: string;
    SignUpCode: string;
    HomeVisitCategoryName: string;
    PresentLocation: string;
    Sprinkle: string;
    Team: string;
    Comments: string;
    FirstName: string;
    LastName: string;
    Email: string;
    Phone: string;
    Home: string;
    Locked: boolean;
    Planned: boolean;
    Parking: string;
    CommentsRequest: string;
    EboekhoudenRelationId: string;
    EboekhoudenInvoiceId: string;
    EboekhoudenRelationCode: string;
    EboekhoudenInvoiceNumber: string;
    Children: any[];
    Adults: any[];
}

function AdminHomeVisitDetailPage() {
    const {Url } = useContext(ApiContext)
    const { token } = useAuth();
    const [homeVisitDetails, setHomeVisitDetails] = useState<HomeVisitDetails | any>(null)
    const [loading, setLoading] = useState(false)
    const [dataAvailable, setDataAvailable] = useState(false)
    const [openEditHomeVisitModal, setOpenEditHomeVisitModal] = useState(false);
    const [openEditChildModal, setOpenEditChildModal] = useState(false);
    const [openEditAdultModal, setOpenEditAdultModal] = useState(false);
    const [message, setMessage] = useState('');
    const [rowData, setRowData] = useState([])
    const [mailKey, setMailKey] = useState(0); // Add this state to force re-render
    const [eBoekhoudenRelationId, setEBoekhoudenRelationId] = useState('');
    const [actionBusy, setActionBusy] = useState(false);
    // Ref guard so rapid double clicks can't start an action twice before re-render
    const actionBusyRef = useRef(false);
    const navigate = useNavigate();

    // Runs one action at a time; all action buttons are disabled while it runs
    const runAction = async (action: () => Promise<void>) => {
        if (actionBusyRef.current) return;
        actionBusyRef.current = true;
        setActionBusy(true);
        try {
            await action();
        } finally {
            actionBusyRef.current = false;
            setActionBusy(false);
        }
    };

    // Get ID from URL
    const params = useParams();

    useEffect(() => {
        if (homeVisitDetails) {
            setEBoekhoudenRelationId(homeVisitDetails.EboekhoudenRelationId || '');
        }
    }, [homeVisitDetails]);

    const getColumnsChildern = () => [
        {
            Header: "ID",
            accessor: "ID",
        },
        {
            Header: "Voornaam",
            accessor: "FirstName",
        },
        {
            Header: "Achternaam",
            accessor: "LastName",
        },
        {
            Header: "Leeftijd",
            accessor: "Age",
        },
        {
            Header: "Acties",
            id: "actions",
            Cell: ({ row }: { row: { original: any } }) => (
                <div className="flex gap-2">
                    <EditButton
                        onClick={() => handleRowData(row.original)}
                    />
                </div>
            )
        }
    ];

    const getColumnsAdults = () => [
        {
            Header: "Voornaam",
            accessor: "FirstName",
        },
        {
            Header: "Achternaam",
            accessor: "LastName",
        },
        {
            Header: "Acties",
            id: "actions",
            Cell: ({ row }: { row: { original: any } }) => (
                <div className="flex gap-2">
                    <EditButton
                        onClick={() => handleRowDataAdult(row.original)}
                    />
                </div>
            )
        }
    ];

    async function getHomeVisitDetails(){
        if (token) {
            try {
                const res = await fetch(Url + 'admin/home-visit-details/' + params.id, {
                    method: 'GET',
                    headers: {
                        'content-type': 'application/json',
                        'Authorization': "Bearer " + token,
                    },
                });
                if (res.status === 200) {
                    const data = await res.json();
                    if (data && !data.message) {
                        setHomeVisitDetails(data)
                        setDataAvailable(true)
                        return data;
                    } else {
                        setDataAvailable(false)
                        setHomeVisitDetails(data)
                        return data;
                    }
                } else {
                    setDataAvailable(false)
                    return {
                        "message": "Geen informatie voor dit huisbezoek beschikbaar."
                    }
                }
            } catch (err) {
                console.error(err);
                setDataAvailable(false);
            } finally {
                setLoading(false)
            }
        }
    }

    useEffect(() => {
            setLoading(true)
            getHomeVisitDetails()
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [token, Url]);
    useEffect(() => {
            getHomeVisitDetails()
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [openEditHomeVisitModal, openEditChildModal, openEditAdultModal]);

    // Add this useEffect to update mail editor when homeVisitDetails changes
    useEffect(() => {
        if (homeVisitDetails?.Email) {
            setMailKey(prev => prev + 1);
        }
    }, [homeVisitDetails?.Email, homeVisitDetails?.FirstName, homeVisitDetails?.LastName, homeVisitDetails?.Date, homeVisitDetails?.StartTime, homeVisitDetails?.Address, homeVisitDetails?.City, homeVisitDetails?.Payed, homeVisitDetails?.Children, homeVisitDetails?.HomeVisitCategoryName, homeVisitDetails?.PasswordResetToken]);

    function handleRowData (rowDataInput: any){
        setRowData(rowDataInput)
        if (rowData){
            setOpenEditChildModal(true)
        }
    }
    function handleRowDataAdult (rowDataInput: any){
        setRowData(rowDataInput)
        if (rowData){
            setOpenEditAdultModal(true)
        }
    }

    const handleDownloadPDF = () => {
        return fetch(Url + `admin/home-visits-export-pdf?id=${params.id}`, {
            method: 'GET',
            headers: {
                'content-type': 'application/json',
                'Authorization': "Bearer " + token,
            }
        })
            .then((response) => response.blob())
            .then((blob) => {
                const url = window.URL.createObjectURL(new Blob([blob]));
                const link = document.createElement("a");
                link.href = url;
                link.download = `HomeVisit_${params.id}_Details.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch((error) => {
                console.error("Error fetching the file:", error);
            });
    };

    const handleUpdateRelationId = async () => {
        try {
            const response = await fetch(Url + 'admin/home-visit-relation', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    homeVisitId: homeVisitDetails.ID,
                    relationId: eBoekhoudenRelationId
                })
            });
            if (response.ok) {
                setMessage('Relation ID bijgewerkt!');
                setTimeout(() => setMessage(''), 3000);
                getHomeVisitDetails();
            } else {
                throw new Error('Failed to update relation ID');
            }
        } catch (error) {
            console.error('Error updating relation ID:', error);
            setMessage('Fout bij bijwerken Relation ID');
        }
    };

    const handleCreateInvoice = async () => {
        setMessage('Factuur aanmaken...');
        try {
            const invoiceRes = await fetch(Url + 'admin/home-visit/' + homeVisitDetails.ID + '/create-invoice', {
                method: 'POST',
                headers: {
                    'content-type': 'application/json',
                    'Authorization': "Bearer " + token,
                },
            });

            if (!invoiceRes.ok) {
                throw new Error('Het aanmaken van de factuur in e-boekhouden is mislukt');
            }

            await getHomeVisitDetails();
            setMessage('Factuur succesvol aangemaakt!');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Error creating invoice:', error);
            setMessage('Het aanmaken van de factuur is mislukt');
        }
    };

    const handleSend = async (emailData: any) => {
        setMessage('Bezig met versturen...');
        try {
            let finalBody = emailData.body;

            // If this is the "Factuur en Grote Boek" email (detected by subject or presence of token placeholder/ONBEKEND)
            // Or better, just always try to get a fresh token if the user email is available
            if (emailData.subject.toLowerCase().includes('factuur') && homeVisitDetails?.Email) {
                setMessage('Factuur controleren/aanmaken...');
                const invoiceRes = await fetch(Url + 'admin/home-visit/' + homeVisitDetails.ID + '/create-invoice', {
                    method: 'POST',
                    headers: {
                        'content-type': 'application/json',
                        'Authorization': "Bearer " + token,
                    },
                });
                
                if (!invoiceRes.ok) {
                    throw new Error('Het aanmaken van de factuur in e-boekhouden is mislukt');
                }
                
                // Refresh home visit details to get the new InvoiceId
                await getHomeVisitDetails();
                
                setMessage('Token genereren...');
                const tokenRes = await fetch(Url + 'admin/generate-password-token/' + homeVisitDetails.Email, {
                    method: 'GET',
                    headers: {
                        'content-type': 'application/json',
                        'Authorization': "Bearer " + token,
                    },
                });
                const tokenData = await tokenRes.json();
                if (tokenData.token) {
                    // Replace 'ONBEKEND' or any existing token with the fresh one in the body
                    // We also try to replace the generic placeholder [token] just in case
                    const oldToken = homeVisitDetails.PasswordResetToken || 'ONBEKEND';
                    finalBody = finalBody.split(oldToken).join(tokenData.token);
                    finalBody = finalBody.split('[token]').join(tokenData.token);
                    
                    // Update state so the UI reflects the sent token
                    setHomeVisitDetails((prev: any) => ({
                        ...prev,
                        PasswordResetToken: tokenData.token
                    }));
                }
            }

            setMessage('Bezig met versturen...');
            const response = await fetch(Url + 'admin/send-mail', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    sender: 'info@ridderendeprins.nl',
                    recipient: emailData.to,
                    cc: emailData.cc,
                    subject: emailData.subject,
                    htmlBody: finalBody
                })
            });

            if (response.ok) {
                setMessage('Email succesvol verzonden!');
                setTimeout(() => setMessage(''), 3000);
            } else {
                throw new Error('Failed to send email');
            }
        } catch (error) {
            console.error('Error sending email:', error);
            setMessage('Het versturen van de email is mislukt');
        }
    };

    let dataTable
    if (dataAvailable && homeVisitDetails?.Children){
        dataTable =  (<Table tableData={homeVisitDetails.Children} getColumns={getColumnsChildern()} storageKey={"HomeVisitDetailsChildren"}></Table>)
    }else {
        dataTable = <div>Er zijn nog geen kinderen ingeschreven voor dit bezoek</div>
    }
    let dataTableAdults
    if (dataAvailable && homeVisitDetails?.Adults){
        dataTableAdults =  (<Table tableData={homeVisitDetails.Adults} getColumns={getColumnsAdults()} storageKey={"HomeVisitDetailsAdults"}></Table>)
    }else {
        dataTableAdults = <div>Er zijn nog geen volwassenen ingeschreven voor dit bezoek</div>
    }


    let lockedInfo
    if (dataAvailable && homeVisitDetails?.Locked){
        lockedInfo = (
            <div className=" my-4 text-xl font-extrabold text-red-600 ">GROTE BOEK VOOR DIT BEZOEK STAAT DICHT! INFORMATIE KAN NIET GEWIJZIGD WORDEN DOOR KLANTEN!</div>
        )
    }else{
        lockedInfo = (<div className="my-4 text-xl font-extrabold text-red-600">LOCK NIET ACTIEF INFORMATIE KAN DOOR KLANTEN GEWIJZIGD WORDEN!</div>)
    }
    let plannedInfo
    if (dataAvailable && homeVisitDetails?.Planned){
        plannedInfo = (
            <div className=" my-4 text-xl font-extrabold text-red-600 ">TIJDSTIP HEEFT DE STATUS BEVESTIGD. KLANT ZIET HET DEFINITIEVE TIJDSTIP</div>
        )
    }else{
        plannedInfo = (<div className="my-4 text-xl font-extrabold text-red-600">TIJDSTIP IS NOG NIET BEVESTIGD. KLANT ZIET ALLEEN HET TIJDSTIPBLOK</div>)
    }
    let dataViewer
    if (dataAvailable) {
        dataViewer = (
            <BoxLayout>
                <div className="mb-4 mt-4">
                    <DefaultButton content="Terug"
                                   onClick={() => navigate("/admin/home-visits/", {replace: false})}></DefaultButton>
                </div>
                <H2Header headerText={homeVisitDetails.Home}/>
                <H2Header headerText="Algemeen"/>
                {lockedInfo}
                <div className="text-xl">Voornaam: {homeVisitDetails.FirstName}</div>
                <div className="text-xl">Achternaam: {homeVisitDetails.LastName}</div>
                <div className="text-xl">Email: {homeVisitDetails.Email}</div>
                <div className="text-xl">Telefoonnummer: {homeVisitDetails.Phone}</div>
                <div className="text-xl">Soort optreden: {homeVisitDetails.HomeVisitCategoryName}</div>
                <div className="text-xl">Cadeaulocatie: {homeVisitDetails.PresentLocation}</div>
                <div className="text-xl">Strooien: {homeVisitDetails.Sprinkle}</div>
                <div className="text-xl">Team: {homeVisitDetails.Team}</div>
                <div className="text-xl">Bijzonderheden: {homeVisitDetails.Comments}</div>
                <div className="text-xl">Parkeren: {homeVisitDetails.Parking}</div>
                <div className="text-xl">Opmerkingen van klant tijdens de aanvraag: {homeVisitDetails.CommentsRequest}</div>
                <div className="text-xl flex items-center gap-2 mt-2">
                    E-boekhouden Relatiecode:
                    {homeVisitDetails.EboekhoudenRelationCode ? (
                        <span className="font-bold">{homeVisitDetails.EboekhoudenRelationCode}</span>
                    ) : (
                        <span>Onbekend</span>
                    )}
                </div>
                <div className="text-xl flex items-center gap-2 mt-2">
                    E-boekhouden Relation ID: 
                    <input 
                        className="border p-1" 
                        value={eBoekhoudenRelationId} 
                        onChange={(e) => setEBoekhoudenRelationId(e.target.value)} 
                    />
                    <DefaultButton content="Opslaan" onClick={() => runAction(handleUpdateRelationId)} disabled={actionBusy} />
                </div>
                <div className="text-xl flex items-center gap-2 mt-2">
                    E-boekhouden Factuurnummer:
                    {homeVisitDetails.EboekhoudenInvoiceId ? (
                        <span className="font-bold">{homeVisitDetails.EboekhoudenInvoiceNumber || homeVisitDetails.EboekhoudenInvoiceId}</span>
                    ) : (
                        <>
                            <span>Geen factuur aangemaakt</span>
                            <DefaultButton content="Factuur aanmaken" onClick={() => runAction(handleCreateInvoice)} disabled={actionBusy} />
                        </>
                    )}
                </div>
                <div className="mb-4 mt-4">
                    <DefaultButton content="Bewerken" onClick={() => setOpenEditHomeVisitModal(true)}></DefaultButton>
                </div>
                <div className="mb-4">
                    <DefaultButton content="Export PDF" onClick={() => runAction(handleDownloadPDF)} disabled={actionBusy}></DefaultButton>
                </div>

                <H2Header headerText="Datum en tijd"/>
                {plannedInfo}
                <div className="text-xl">Datum: {homeVisitDetails.Date}</div>
                <div className="text-xl">Gewenste starttijd opgegeven door de klant: {homeVisitDetails.RequestedStartTime}</div>
                <div className="text-xl">Starttijd: {homeVisitDetails.StartTime}</div>
                <div className="text-xl">Begin Tijdslot: {homeVisitDetails.StartTimeSlot}</div>
                <div className="text-xl">Eind Tijdslot: {homeVisitDetails.EndTimeSlot}</div>

                <H2Header headerText="Locatie"/>
                <div className="text-xl">Adres: {homeVisitDetails.Address}</div>
                <div className="text-xl">Postcode: {homeVisitDetails.ZipCode}</div>
                <div className="text-xl">Plaats: {homeVisitDetails.City}</div>


                <H2Header headerText="Ingeschreven kinderen"/>
                {dataTable}
                <H2Header headerText="Ingeschreven volwassenen"/>
                {dataTableAdults}



                <H2Header headerText="Nieuwe Email - Factuur en Grote Boek" />
                <MailEditor
                    key={mailKey + 1000} // Different key to ensure separate instance
                    onSend={(emailData: any) => runAction(() => handleSend(emailData))}
                    disabled={actionBusy}
                    sendButtonText="Factuur aanmaken en mail verzenden"
                    initialData={{
                        to: homeVisitDetails.Email,
                        cc: 'info@ridderendeprins.nl',
                        subject: 'Huisbezoek fam. ' + homeVisitDetails.LastName + ' - factuur en Grote Boek',
                        body: (generateInvoiceAndGroteBoekEmailContent({
                            naam: homeVisitDetails.FirstName,
                            email: homeVisitDetails.Email,
                            token: homeVisitDetails.PasswordResetToken || 'ONBEKEND',
                            plaats: homeVisitDetails.City,
                            wachtwoord: 'WACHTWOORD_HIER', // You may want to fetch this from the API or another source
                            factuurInBijlage: true,
                            baseUrl: window.location.origin
                        }) as any).html
                    }}
                />
                {message && (
                    <div className={`my-4 p-3 rounded ${message.includes('mislukt') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {message}
                    </div>
                )}
                <H2Header headerText="Nieuwe Email - Laatste informatie" />
                <MailEditor
                    key={mailKey} // Add key prop to force re-mount
                    onSend={(emailData: any) => runAction(() => handleSend(emailData))}
                    disabled={actionBusy}
                    initialData={{
                        to: homeVisitDetails.Email,
                        cc: 'info@ridderendeprins.nl',
                        subject: 'Huisbezoek fam. ' + homeVisitDetails.LastName + ' - laatste informatie' ,
                        body: generateFinalVisitInfoEmailContent({
                            naam: homeVisitDetails.FirstName,
                            typeBezoek: homeVisitDetails.HomeVisitCategoryName,
                            datum: homeVisitDetails.Date,
                            starttijd: homeVisitDetails.StartTime,
                            adres: homeVisitDetails.Address,
                            plaats: homeVisitDetails.City,
                            factuurBetaald: !!homeVisitDetails.Payed,  // Will show invoice reminder
                            groteBoekIngevuld: !!(homeVisitDetails.Children && homeVisitDetails.Children.length > 0) // Will show "ingevuld" message
                        }).html as any
                    }}
                />
                {message && (
                    <div className={`my-4 p-3 rounded ${message.includes('mislukt') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {message}
                    </div>
                )}

            </BoxLayout>
        )
    } else {
        dataViewer = (
            <BoxLayout>
            <div>Geen data voor dit bezoek in de database</div>
            </BoxLayout>
        )
    }
    return (
        <DefaultLayout>
            <EditHomeVisitModal openModal={openEditHomeVisitModal} setOpenModal={setOpenEditHomeVisitModal}
                                visitInfo={homeVisitDetails}/>
            <EditChildModal openModal={openEditChildModal} setOpenModal={setOpenEditChildModal} welcomePage={false} rowData={rowData} admin={true}/>
            <EditAdultModal openModal={openEditAdultModal} setOpenModal={setOpenEditAdultModal} rowData={rowData} admin={true}/>
            {loading ? (
                <BoxLayout>
                    <div>Loading...</div></BoxLayout>

            ) : (
                <div className="flex flex-col overflow-auto py-4 sm:py-0">
                    {dataViewer}
                </div>
            )}
        </DefaultLayout>
    )
}
export default AdminHomeVisitDetailPage